import { Image } from 'expo-image';
import { usePathname, useRouter } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import React, { useEffect } from 'react';
import { Platform, Pressable, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { UnistylesRuntime, useStyles } from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import { useAdmin } from '@/api/partners/admins/use-admin';
import { isDesktop, signOut, useAuth } from '@/core';
import { useHasOrganization } from '@/core/hooks/use-has-organization';
import useSettingsStore from '@/stores/use-settings-store';
import { Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Car, Docs, Person } from '@/ui/icons';
import { Bolt } from '@/ui/icons/bolt';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { ChevronDown } from '@/ui/icons/chevron-down';
import { ChevronUp } from '@/ui/icons/chevron-up';
import { Dashboard as DashboardIcon } from '@/ui/icons/dashboard';
import { Dollar } from '@/ui/icons/dollar';
import { Dots } from '@/ui/icons/dots';
import { EonIcon } from '@/ui/icons/eon-icon';
import { List } from '@/ui/icons/list';
import { Marker } from '@/ui/icons/marker';
import { Switch } from '@/ui/icons/switch';
import { MenuItem } from '@/ui/menu-item';

enum PartnerSidebarRoute {
  Dashboard = '/partner/dashboard',
  Cars = '/partner/cars',
  Reservations = '/partner/reservations',
  Customers = '/partner/customers',
  Calendar = '/partner/reservations/calendar',
  BankAccount = '/partner/more/bank-account',
  Locations = '/partner/more/locations',
  Payouts = '/partner/payouts',
  JoinEonOne = '/partner/join-eon-one',
  EonOneSettings = '/partner/eon-one/settings',
  EonOneIntegrations = '/partner/eon-one/integrations',
  Profile = '/partner/more/profile',
  MobileApp = '/get-the-app',
  Support = '/partner/more/support-faq',
  ReferAFriend = '/partner/more/referrals',
  SubmitFeedback = '/partner/more/submit-feedback',
  WhatsNew = '/partner/more/whats-new',
}

export default function PartnerSidebar() {
  const { theme } = useStyles();
  const router = useRouter();
  const status = useAuth.use.status();
  const pathname = usePathname();
  const [activeRoute, setActiveRoute] =
    React.useState<PartnerSidebarRoute | null>();
  const [expandEonOne, setExpandEonOne] = React.useState(false);
  const [expandAccount, setExpandAccount] = React.useState(false);
  const [expandResources, setExpandResources] = React.useState(false);
  const [expandMore, setExpandMore] = React.useState(false);
  const setPartnerMode = useSettingsStore((state) => state.setPartnerMode);
  const { hasOrganization, canUseEonOne } = useHasOrganization();
  const { data: admin } = useAdmin();

  useEffect(() => {
    if (pathname.startsWith(PartnerSidebarRoute.Dashboard)) {
      setActiveRoute(PartnerSidebarRoute.Dashboard);
    } else if (pathname.startsWith(PartnerSidebarRoute.Cars)) {
      setActiveRoute(PartnerSidebarRoute.Cars);
    } else if (pathname.startsWith(PartnerSidebarRoute.Calendar)) {
      setActiveRoute(PartnerSidebarRoute.Calendar);
    } else if (pathname.startsWith(PartnerSidebarRoute.Reservations)) {
      setActiveRoute(PartnerSidebarRoute.Reservations);
    } else if (pathname.startsWith(PartnerSidebarRoute.Customers)) {
      setActiveRoute(PartnerSidebarRoute.Customers);
    } else if (pathname.startsWith(PartnerSidebarRoute.BankAccount)) {
      setActiveRoute(PartnerSidebarRoute.BankAccount);
    } else if (pathname.startsWith(PartnerSidebarRoute.Locations)) {
      setActiveRoute(PartnerSidebarRoute.Locations);
    } else if (pathname.startsWith(PartnerSidebarRoute.Payouts)) {
      setActiveRoute(PartnerSidebarRoute.Payouts);
    } else if (pathname.startsWith(PartnerSidebarRoute.EonOneSettings)) {
      setActiveRoute(PartnerSidebarRoute.EonOneSettings);
      setExpandEonOne(true);
    } else if (pathname.startsWith(PartnerSidebarRoute.EonOneIntegrations)) {
      setActiveRoute(PartnerSidebarRoute.EonOneIntegrations);
      setExpandEonOne(true);
    } else if (pathname.startsWith(PartnerSidebarRoute.JoinEonOne)) {
      setActiveRoute(PartnerSidebarRoute.JoinEonOne);
    } else if (pathname.startsWith(PartnerSidebarRoute.Profile)) {
      setActiveRoute(PartnerSidebarRoute.Profile);
    } else if (pathname.startsWith(PartnerSidebarRoute.Support)) {
      setActiveRoute(PartnerSidebarRoute.Support);
    } else if (pathname.startsWith(PartnerSidebarRoute.ReferAFriend)) {
      setActiveRoute(PartnerSidebarRoute.ReferAFriend);
    } else if (pathname.startsWith(PartnerSidebarRoute.SubmitFeedback)) {
      setActiveRoute(PartnerSidebarRoute.SubmitFeedback);
    } else if (pathname.startsWith(PartnerSidebarRoute.WhatsNew)) {
      setActiveRoute(PartnerSidebarRoute.WhatsNew);
    }
  }, [pathname]);

  const handleNavigate = (route: PartnerSidebarRoute) => {
    setActiveRoute(route);
    router.replace(route);

    setTimeout(() => {
      router.replace(route);
    }, 50);
  };

  const { data: customer, refetch: refetchCustomer } = useCustomer({
    enabled: false,
  });

  if (!isDesktop()) {
    return null;
  }

  return (
    <View
      style={{
        backgroundColor: theme.colors.background_2,
        width: 250,
        height: '100%',
        borderRightWidth: 1,
        borderRightColor: theme.colors.card.default.borderColor,
      }}
    >
      <View
        style={{
          borderBottomColor: theme.colors.card.default.borderColor,
          borderBottomWidth: 1,
        }}
      >
        <Pressable
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            padding: 24,
            paddingBottom: 16,
            gap: 8,
          }}
          onPress={(e) => {
            e.preventDefault();
            handleNavigate(PartnerSidebarRoute.Dashboard);
          }}
        >
          <Image
            style={{ width: 110, height: 45 }}
            source={
              UnistylesRuntime.themeName === 'dark'
                ? require('@/../public/images/logos/partner-logo-dark.png')
                : require('@/../public/images/logos/partner-logo-light.png')
            }
            contentFit="contain"
          />
        </Pressable>
      </View>

      <View
        style={{
          padding: 16,
          borderBottomColor: theme.colors.card.default.borderColor,
          borderBottomWidth: 1,
          gap: 4,
        }}
      >
        <MenuItem
          leftIcon={<DashboardIcon />}
          leftIconSize={14}
          leftIconFillColor={theme.colors.icon.color}
          leftIconType="naked"
          tx="partner.sidebar.dashboard"
          txStyle={{
            color:
              activeRoute === PartnerSidebarRoute.Dashboard
                ? theme.colors.text.primary
                : theme.colors.text.secondary,
          }}
          onPress={(e) => {
            e.preventDefault();
            handleNavigate(PartnerSidebarRoute.Dashboard);
          }}
          style={{ width: '100%', padding: 4, paddingLeft: 8 }}
          active={activeRoute === PartnerSidebarRoute.Dashboard}
        />

        <MenuItem
          leftIcon={<Car />}
          leftIconSize={14}
          leftIconFillColor={theme.colors.icon.color}
          leftIconType="naked"
          tx="partner.sidebar.cars"
          txStyle={{
            color:
              activeRoute === PartnerSidebarRoute.Cars
                ? theme.colors.text.primary
                : theme.colors.text.secondary,
          }}
          onPress={(e) => {
            e.preventDefault();
            handleNavigate(PartnerSidebarRoute.Cars);
          }}
          style={{ width: '100%', padding: 4, paddingLeft: 8 }}
          active={activeRoute === PartnerSidebarRoute.Cars}
        />

        <MenuItem
          leftIcon={<List />}
          leftIconSize={14}
          leftIconFillColor={theme.colors.icon.color}
          leftIconType="naked"
          tx="partner.sidebar.reservations"
          txStyle={{
            color:
              activeRoute === PartnerSidebarRoute.Reservations
                ? theme.colors.text.primary
                : theme.colors.text.secondary,
          }}
          onPress={(e) => {
            e.preventDefault();
            handleNavigate(PartnerSidebarRoute.Reservations);
          }}
          style={{ width: '100%', padding: 4, paddingLeft: 8 }}
          active={activeRoute === PartnerSidebarRoute.Reservations}
        />

        {hasOrganization ? (
          <MenuItem
            leftIcon={<Person />}
            leftIconSize={14}
            leftIconFillColor={theme.colors.icon.color}
            leftIconType="naked"
            tx="partner.sidebar.customers"
            txStyle={{
              color:
                activeRoute === PartnerSidebarRoute.Customers
                  ? theme.colors.text.primary
                  : theme.colors.text.secondary,
            }}
            onPress={(e) => {
              e.preventDefault();
              handleNavigate(PartnerSidebarRoute.Customers);
            }}
            style={{ width: '100%', padding: 4, paddingLeft: 8 }}
            active={activeRoute === PartnerSidebarRoute.Customers}
          />
        ) : null}

        <MenuItem
          leftIcon={<CalendarIcon />}
          leftIconSize={14}
          leftIconFillColor={theme.colors.icon.color}
          leftIconType="naked"
          tx="partner.sidebar.calendar"
          txStyle={{
            color:
              activeRoute === PartnerSidebarRoute.Calendar
                ? theme.colors.text.primary
                : theme.colors.text.secondary,
          }}
          onPress={(e) => {
            e.preventDefault();
            handleNavigate(PartnerSidebarRoute.Calendar);
          }}
          style={{ width: '100%', padding: 4, paddingLeft: 8 }}
          active={activeRoute === PartnerSidebarRoute.Calendar}
        />

        <MenuItem
          leftIcon={<Marker />}
          leftIconSize={14}
          leftIconFillColor={theme.colors.icon.color}
          leftIconType="naked"
          tx="partner.sidebar.locations"
          txStyle={{
            color:
              activeRoute === PartnerSidebarRoute.Locations
                ? theme.colors.text.primary
                : theme.colors.text.secondary,
          }}
          onPress={(e) => {
            e.preventDefault();
            handleNavigate(PartnerSidebarRoute.Locations);
          }}
          style={{ width: '100%', padding: 4, paddingLeft: 8 }}
          active={activeRoute === PartnerSidebarRoute.Locations}
        />

        <MenuItem
          leftIcon={<Dollar />}
          leftIconSize={14}
          leftIconFillColor={theme.colors.icon.color}
          leftIconType="naked"
          tx="partner.sidebar.payouts"
          txStyle={{
            color:
              activeRoute === PartnerSidebarRoute.Payouts
                ? theme.colors.text.primary
                : theme.colors.text.secondary,
          }}
          onPress={(e) => {
            e.preventDefault();
            handleNavigate(PartnerSidebarRoute.Payouts);
          }}
          style={{ width: '100%', padding: 4, paddingLeft: 8 }}
          active={activeRoute === PartnerSidebarRoute.Payouts}
        />

        <View>
          <MenuItem
            leftIcon={<EonIcon />}
            leftIconSize={14}
            leftIconFillColor={theme.colors.icon.color}
            leftIconType="naked"
            tx="partner.sidebar.eonOne"
            txStyle={{
              color: [
                PartnerSidebarRoute.JoinEonOne,
                PartnerSidebarRoute.EonOneSettings,
                PartnerSidebarRoute.EonOneIntegrations,
              ].includes(activeRoute!)
                ? theme.colors.text.primary
                : theme.colors.text.secondary,
            }}
            onPress={(e) => {
              e.preventDefault();
              if (!canUseEonOne) {
                handleNavigate(PartnerSidebarRoute.JoinEonOne);
                return;
              }
              setExpandEonOne(!expandEonOne);
            }}
            style={{ width: '100%', padding: 4, paddingLeft: 8 }}
            active={activeRoute === PartnerSidebarRoute.JoinEonOne}
            rightIcon={
              canUseEonOne ? (
                expandEonOne ? (
                  <ChevronDown />
                ) : (
                  <ChevronUp />
                )
              ) : null
            }
            rightIconFillColor={theme.colors.icon.color}
          />

          {expandEonOne ? (
            <View style={{ paddingLeft: 24 }}>
              <MenuItem
                tx="partner.sidebar.settings"
                txStyle={{
                  color:
                    activeRoute === PartnerSidebarRoute.EonOneSettings
                      ? theme.colors.text.primary
                      : theme.colors.text.secondary,
                }}
                onPress={(e) => {
                  e.preventDefault();
                  handleNavigate(PartnerSidebarRoute.EonOneSettings);
                }}
                style={{ width: '100%', padding: 4, paddingLeft: 8 }}
                active={activeRoute === PartnerSidebarRoute.EonOneSettings}
              />

              <MenuItem
                tx="partner.sidebar.integrations"
                txStyle={{
                  color:
                    activeRoute === PartnerSidebarRoute.EonOneIntegrations
                      ? theme.colors.text.primary
                      : theme.colors.text.secondary,
                }}
                onPress={(e) => {
                  e.preventDefault();
                  handleNavigate(PartnerSidebarRoute.EonOneIntegrations);
                }}
                style={{ width: '100%', padding: 4, paddingLeft: 8 }}
                active={activeRoute === PartnerSidebarRoute.EonOneIntegrations}
              />
            </View>
          ) : null}
        </View>
      </View>

      <View
        style={{
          padding: 16,
          borderBottomColor: theme.colors.card.default.borderColor,
          borderBottomWidth: 1,
          gap: 8,
        }}
      >
        <View>
          <MenuItem
            leftIcon={<Person />}
            leftIconSize={14}
            leftIconFillColor={theme.colors.icon.color}
            leftIconType="naked"
            tx="partner.sidebar.account"
            txStyle={{
              color:
                activeRoute === PartnerSidebarRoute.Profile
                  ? theme.colors.text.primary
                  : theme.colors.text.secondary,
            }}
            onPress={(e) => {
              e.preventDefault();
              setExpandAccount(!expandAccount);
            }}
            style={{ width: '100%', padding: 4 }}
            rightIcon={expandAccount ? <ChevronDown /> : <ChevronUp />}
            rightIconFillColor={theme.colors.icon.color}
          />

          {expandAccount ? (
            <View style={{ paddingLeft: 24 }}>
              <MenuItem
                tx="partner.sidebar.profile"
                onPress={(e) => {
                  e.preventDefault();
                  handleNavigate(PartnerSidebarRoute.Profile);
                }}
                txStyle={{
                  color:
                    activeRoute === PartnerSidebarRoute.Profile
                      ? theme.colors.text.primary
                      : theme.colors.text.secondary,
                }}
                style={{ width: '100%', padding: 4, paddingLeft: 8 }}
                active={activeRoute === PartnerSidebarRoute.Profile}
              />

              <MenuItem
                tx="partner.sidebar.bankAccount"
                txStyle={{ color: theme.colors.text.secondary }}
                onPress={async (e) => {
                  e.preventDefault();
                  handleNavigate(PartnerSidebarRoute.BankAccount);
                }}
                style={{ width: '100%', padding: 4, paddingLeft: 8 }}
              />
            </View>
          ) : null}
        </View>

        <View>
          <MenuItem
            leftIcon={<Docs />}
            leftIconSize={14}
            leftIconFillColor={theme.colors.icon.color}
            leftIconType="naked"
            tx="partner.sidebar.resources"
            txStyle={{
              color: [
                PartnerSidebarRoute.Support,
                PartnerSidebarRoute.MobileApp,
              ].includes(activeRoute!)
                ? theme.colors.text.primary
                : theme.colors.text.secondary,
            }}
            onPress={(e) => {
              e.preventDefault();
              setExpandResources(!expandResources);
            }}
            style={{ width: '100%', padding: 4 }}
            rightIcon={expandResources ? <ChevronDown /> : <ChevronUp />}
            rightIconFillColor={theme.colors.icon.color}
          />

          {expandResources ? (
            <View style={{ paddingLeft: 24 }}>
              <MenuItem
                tx="partner.sidebar.howEonWorks"
                txStyle={{ color: theme.colors.text.secondary }}
                onPress={(e) => {
                  e.preventDefault();
                  router.push('/partner/cars?onboarding=1');
                }}
                style={{ width: '100%', padding: 4, paddingLeft: 8 }}
              />

              <MenuItem
                tx="partner.sidebar.mobileApp"
                txStyle={{
                  color:
                    activeRoute === PartnerSidebarRoute.MobileApp
                      ? theme.colors.text.primary
                      : theme.colors.text.secondary,
                }}
                onPress={(e) => {
                  e.preventDefault();
                  handleNavigate(PartnerSidebarRoute.MobileApp);
                }}
                style={{ width: '100%', padding: 4, paddingLeft: 8 }}
                active={activeRoute === PartnerSidebarRoute.MobileApp}
              />

              <MenuItem
                tx="partner.sidebar.ownerGuide"
                txStyle={{ color: theme.colors.text.secondary }}
                onPress={async (e) => {
                  e.preventDefault();

                  const url =
                    'https://eonrides.notion.site/Eon-Guide-For-Owners-30874a0bcebc4894819b5ecf62cf6b4f';

                  if (Platform.OS === 'web') {
                    window.open(url, '_blank');
                  } else {
                    await WebBrowser.openBrowserAsync(url);
                  }
                }}
                style={{ width: '100%', padding: 4, paddingLeft: 8 }}
              />

              <MenuItem
                tx="partner.sidebar.support"
                txStyle={{
                  color:
                    activeRoute === PartnerSidebarRoute.Support
                      ? theme.colors.text.primary
                      : theme.colors.text.secondary,
                }}
                onPress={(e) => {
                  e.preventDefault();
                  handleNavigate(PartnerSidebarRoute.Support);
                }}
                style={{ width: '100%', padding: 4, paddingLeft: 8 }}
                active={activeRoute === PartnerSidebarRoute.Support}
              />
            </View>
          ) : null}
        </View>

        <View>
          <MenuItem
            leftIcon={<Dots />}
            leftIconSize={14}
            leftIconFillColor={theme.colors.icon.color}
            leftIconType="naked"
            tx="partner.sidebar.more"
            txStyle={{
              color: [
                PartnerSidebarRoute.ReferAFriend,
                PartnerSidebarRoute.SubmitFeedback,
              ].includes(activeRoute!)
                ? theme.colors.text.primary
                : theme.colors.text.secondary,
            }}
            onPress={(e) => {
              e.preventDefault();
              setExpandMore(!expandMore);
            }}
            style={{ width: '100%', padding: 4 }}
            rightIcon={expandMore ? <ChevronDown /> : <ChevronUp />}
            rightIconFillColor={theme.colors.icon.color}
          />

          {expandMore ? (
            <View style={{ paddingLeft: 24 }}>
              <MenuItem
                tx="partner.sidebar.referAFriend"
                txStyle={{
                  color:
                    activeRoute === PartnerSidebarRoute.ReferAFriend
                      ? theme.colors.text.primary
                      : theme.colors.text.secondary,
                }}
                onPress={(e) => {
                  e.preventDefault();
                  handleNavigate(PartnerSidebarRoute.ReferAFriend);
                }}
                style={{ width: '100%', padding: 4, paddingLeft: 8 }}
                active={activeRoute === PartnerSidebarRoute.ReferAFriend}
              />

              <MenuItem
                tx="partner.sidebar.submitFeedback"
                txStyle={{
                  color:
                    activeRoute === PartnerSidebarRoute.SubmitFeedback
                      ? theme.colors.text.primary
                      : theme.colors.text.secondary,
                }}
                onPress={(e) => {
                  e.preventDefault();
                  handleNavigate(PartnerSidebarRoute.SubmitFeedback);
                }}
                style={{ width: '100%', padding: 4, paddingLeft: 8 }}
                active={activeRoute === PartnerSidebarRoute.SubmitFeedback}
              />
            </View>
          ) : null}
        </View>

        <MenuItem
          leftIcon={<Bolt />}
          leftIconSize={14}
          leftIconFillColor={theme.colors.icon.color}
          leftIconType="naked"
          tx="partner.sidebar.whatsNew"
          txStyle={{
            color:
              activeRoute === PartnerSidebarRoute.WhatsNew
                ? theme.colors.text.primary
                : theme.colors.text.secondary,
          }}
          onPress={(e) => {
            e.preventDefault();
            handleNavigate(PartnerSidebarRoute.WhatsNew);
          }}
          style={{ width: '100%', padding: 4 }}
          active={activeRoute === PartnerSidebarRoute.WhatsNew}
          badgeCount={admin?.unreadChangelogCount}
        />
      </View>

      <View
        style={{
          padding: 16,
          paddingBottom: 32,
          gap: 8,
          flex: 1,
          justifyContent: 'flex-end',
        }}
      >
        <MenuItem
          tx="partner.sidebar.logOut"
          onPress={() => {
            signOut();
            router.replace('/search');
            Toast.show({
              text1: 'You have logged out',
              type: 'success',
            });
          }}
          style={{ width: '100%', padding: 4 }}
        />

        <MenuItem
          tx="partner.sidebar.switchToGuest"
          txStyle={{
            color: theme.colors.icon.brandColor,
          }}
          leftIcon={<Switch />}
          leftIconSize={14}
          leftIconType="naked"
          leftIconFillColor={theme.colors.icon.color}
          onPress={(e) => {
            e.preventDefault();
            setPartnerMode(false);
            router.replace('/search');
          }}
          style={{ width: '100%', padding: 4 }}
        />

        {status === 'signIn' && customer ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {customer?.firstname || customer?.lastname ? (
              <View
                style={{
                  borderRadius: 32,
                  borderWidth: 1,
                  borderColor: theme.colors.card.hover.borderColor,
                  backgroundColor: theme.colors.card.hover.backgroundColor,
                  width: 40,
                  height: 40,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Text
                  type="bodyBold"
                  style={{ color: theme.colors.icon.color }}
                >
                  {`${customer?.firstname?.[0] || ''}${
                    customer?.lastname?.[0] || ''
                  }`}
                </Text>
              </View>
            ) : (
              <Icon
                type="dressed"
                icon={<Person />}
                fillColor={theme.colors.icon.color}
                width={20}
                height={20}
                style={{ borderRadius: 20 }}
              />
            )}

            <View>
              <Text>{`${customer?.firstname || ''} ${
                customer?.lastname || ''
              }`}</Text>
              {customer?.email ? (
                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {customer.email}
                </Text>
              ) : null}
            </View>
          </View>
        ) : (
          <Icon
            type="dressed"
            icon={<Person />}
            fillColor={theme.colors.icon.color}
            width={20}
            height={20}
            style={{ borderRadius: 20 }}
          />
        )}
      </View>
    </View>
  );
}
